import {
  Autocomplete,
  Icon,
  Table,
  TableBody,
  TableRow,
  Card,
  TextField,
  Grid,
  Box,
  Typography,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from "react-table";
import WDBox from "../../../MaterialComponents/WDBox";
import WDPagination from "../../../MaterialComponents/WDPagination";
import WDTypography from "../../../MaterialComponents/WDTypography";
import DataTableBodyCell from "./DataTableBodyCell";
import DataTableHeadCell from "./DataTableHeadCell";
import DataFilter from "./FilterOption";
import pxToRem from "../../../Themes/Functions/pxToRem";
import ChipButton from "../../ChipButton";

const DataTable = (props) => {
  const {
    table,
    entriesPerPage,
    isSorted,
    noEndBorder,
    showTotalEntries,
    pagination,
    hiddenColumns,
    showAddButton,
    addButtonOnClick,
  } = props;
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);
  const filters = props.tableFilters || [];

  const defaultFilters = filters
    .filter((filter) => filter.defaultValue)
    .map((filter) => {
      return {
        id: filter.accessor,
        value: filter.defaultValue,
      };
    });

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, filters: defaultFilters },
      autoResetFilters: false,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);
  const setEntriesPerPage = (value) => setPageSize(value);

  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;
  let entriesEnd;
  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  const entries = ["5", "10", "15", "20", "25"];

  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  const renderPagination = pageOptions.map((option) => (
    <WDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </WDPagination>
  ));

  const _onFilterChange = (accessor, value) => {
    setFilter(accessor, value);
  };

  const _renderFilter = (filterInfo) => {
    return (
      <Grid item xs={3.33}>
        <DataFilter data={data} filterInfo={filterInfo} onChange={_onFilterChange} />
      </Grid>
    );
  };

  const _renderAddButton = () => {
    return (
      <WDBox
        sx={{
          display: "flex",
          position: "absolute",
          top: pxToRem(24),
          right: pxToRem(18),
          width: pxToRem(84),
          height: pxToRem(30),
          borderRadius: pxToRem(20),
          backgroundColor: "green",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
        }}
        variant="gradient"
        bgColor="primary"
        borderRadius="lg"
        onClick={addButtonOnClick}
      >
        <Typography fontSize={12} fontWeight={500} color="#ffffff">
          CREATE
        </Typography>
      </WDBox>
    );
  };

  return (
    <Card sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
      <Box sx={{ display: "flex", flex: 1, overflow: "auto" }}>
        <Table sx={{ minWidth: "100%", tableLayout: "fixed" }} {...getTableProps()}>
          <WDBox component="thead">
            {headerGroups.map((headerGroup) => (
              <TableRow
                {...headerGroup.getHeaderGroupProps()}
                sx={{ height: "32px" }} /* Fixed compact header row height */
              >
                {headerGroup.headers.map((column) => (
                  <DataTableHeadCell
                    {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                    width={column.width ? column.width : "auto"}
                    align={column.align ? column.align : "left"}
                    sorted={isSorted ? column.isSorted ? column.isSortedDesc ? "desc" : "asce" : false : false}
                  >
                    {column.render("Header")}
                  </DataTableHeadCell>
                ))}
              </TableRow>
            ))}
          </WDBox>
          <TableBody {...getTableBodyProps()}>
            {page.map((row, key) => {
              prepareRow(row);
              return (
                <TableRow {...row.getRowProps()} sx={{ height: "32px" }}>
                  {row.cells.map((cell) => (
                    <DataTableBodyCell
                      noBorder={noEndBorder && rows.length - 1 === key}
                      align={cell.column.align ? cell.column.align : "left"}
                      {...cell.getCellProps()}
                    >
                      {cell.render("Cell")}
                    </DataTableBodyCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      <WDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && pageOptions.length === 1 ? 0 : 2}
      >
        <Grid container spacing={3}>
          <Grid item md={3}>
            <Autocomplete
              disableClearable
              value={pageSize.toString()}
              options={entries}
              onChange={(event, newValue) => {
                setEntriesPerPage(parseInt(newValue, 10));
              }}
              size="small"
              renderInput={(params) => <TextField {...params} label={"Page Size"} />}
            />
          </Grid>
          <Grid item md={3}>
            <WDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {rows.length} entries
            </WDTypography>
          </Grid>
          <Grid item md={4}>
            {pageOptions.length > 1 && (
              <WDPagination
                variant={pagination.variant ? pagination.variant : "gradient"}
                color={pagination.color ? pagination.color : "info"}
              >
                {canPreviousPage && (
                  <WDPagination item onClick={() => previousPage()}>
                    <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                  </WDPagination>
                )}
                {renderPagination}
                {canNextPage && (
                  <WDPagination item onClick={() => nextPage()}>
                    <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                  </WDPagination>
                )}
              </WDPagination>
            )}
          </Grid>
          {showAddButton && (
            <Box
              sx={{
                display: "flex",
                flex: 1,
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <ChipButton variant="outlined" clickable onClick={addButtonOnClick}>
                create new
              </ChipButton>
            </Box>
          )}
        </Grid>
      </WDBox>
    </Card>
  );
};

DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
  hiddenColumns: [],
  defaultFilters: [],
};

export default DataTable;
